import { useEffect, useState, useCallback } from "react"

import { useParams } from "react-router-dom"
import { useForm, FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { AdvancedFormsHandler } from "components/forms/advanced-forms-handler"
import { BasicFormsHandler } from "components/forms/basic-forms-handler"
import { ConfirmationModal } from "../../components/confirmation-modal"

import { ContactInformation } from "pages/ticket-details/contact-information"
import { TicketDetailsPlaceholder } from "./ticket-details-placeholder"

import { useLocalStorage } from "hooks/utils/use-local-storage"
import { useNavigateWithParams } from "hooks/navigation/use-navigate-with-params"
import { useRegistrationData } from "hooks/api/details/use-registration-data"
import { useTicketGroupsData } from "hooks/api/tickets/use-ticket-groups-data"
import { useOrderData } from "hooks/api/order/use-order-data"
import { useApplyDiscountMutation } from "hooks/api/order/use-apply-discount-mutation"
import { useRemoveDiscountMutation } from "hooks/api/order/use-remove-discount-mutation"
import { useDeleteOrderMutation } from "hooks/api/order/use-delete-order-mutation"
import { useSubmitFormMutation } from "hooks/api/order/use-submit-form-mutation"
import { useBaseContext } from "contexts/use-base-context"

import { getNavigationPath } from "../../utils/navigation"

import { PlaceOrderFormData } from "types"

const TicketDetails = () => {
  const navigateWithParams = useNavigateWithParams()
  const { uuid } = useParams()
  const { localStorageData, setLocalStorageData } = useLocalStorage()
  const isPreview = window.location.search.includes("isPreview=true")
  const deviceId = localStorage.getItem("device_id") || ""
  const { isAdvancedFormsEnabled } = useRegistrationData()
  const methods = useForm<PlaceOrderFormData>({
    mode: "onChange",
    defaultValues: {
      contactInfo: {
        firstName: "",
        lastName: "",
        email: "",
        confirmEmail: "",
      },
      tickets: {},
    },
  })
  const { refetchRegistrationData, bundleUUID } = useRegistrationData()
  const { t } = useTranslation()
  const { currencySymbol } = useBaseContext()
  const navigateToSelectTickets = useCallback(() => {
    setLocalStorageData(null)
    navigateWithParams(getNavigationPath(`/${uuid}/select-tickets`, isPreview))
  }, [setLocalStorageData, navigateWithParams, uuid, isPreview])

  const {
    orderData,
    isPending,
    total,
    subtotal: amount_subtotal,
    discount: amount_discount,
    discountCode: discount_code,
  } = useOrderData({
    orderUuid: localStorageData?.uuid || "",
    deviceId,
    enabled: !!localStorageData?.uuid,
  })

  const { isLoading: isTicketGroupsLoading } = useTicketGroupsData(bundleUUID)

  const applyDiscount = useApplyDiscountMutation({
    orderUuid: localStorageData?.uuid || "",
    deviceId,
  })

  const removeDiscount = useRemoveDiscountMutation({
    orderUuid: localStorageData?.uuid || "",
    deviceId,
  })

  const deleteOrder = useDeleteOrderMutation({
    deviceId,
    onOrderDeleted: navigateToSelectTickets,
  })

  const submitForm = useSubmitFormMutation({
    orderUuid: localStorageData?.uuid || "",
    orderData,
  })

  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [isNavigatingAway, setIsNavigatingAway] = useState(false)
  const [discountCode, setDiscountCode] = useState("")

  useEffect(() => {
    window.history.pushState(null, "", window.location.href)

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isNavigatingAway) {
        e.preventDefault()
      }
    }

    const handlePopState = () => {
      if (!isNavigatingAway) {
        window.history.pushState(null, "", window.location.href)
        setShowConfirmModal(true)
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    window.addEventListener("popstate", handlePopState)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
      window.removeEventListener("popstate", handlePopState)
    }
  }, [isNavigatingAway])

  useEffect(() => {
    if (localStorageData?.checkoutStarted) {
      navigateWithParams(`/${uuid}/checkout`, {
        order_id: localStorageData?.uuid,
      })
    }
  }, [localStorageData, navigateWithParams, uuid, isPreview])

  const onSubmit = async (data: PlaceOrderFormData) => {
    try {
      // Set a loading state for screen readers
      const loadingMessage = document.createElement("div")
      loadingMessage.setAttribute("role", "status")
      loadingMessage.setAttribute("aria-live", "assertive")
      loadingMessage.className = "sr-only"
      loadingMessage.textContent = t("general.processing")
      document.body.appendChild(loadingMessage)

      // Use the form submission mutation
      await submitForm.mutateAsync(data)

      // Update local storage to indicate that answers were submitted successfully
      // This will allow users to continue to checkout if they close the tab
      setLocalStorageData({
        ...localStorageData,
        answersSubmitted: true,
        checkoutStarted: total > 0,
        lastUpdated: new Date().toISOString(),
      })

      // Check if total is greater than 0, navigate to checkout if it is
      if (total > 0) {
        setIsNavigatingAway(true)
        // Update the loading message for screen readers
        loadingMessage.textContent = t("general.navigating_to_checkout")
      } else {
        // Free order, continue with the original flow
        setIsNavigatingAway(true)
        // Update the loading message for screen readers
        loadingMessage.textContent = t("general.order_successful")
        navigateWithParams(`/${uuid}/success`, {
          order_id: localStorageData?.uuid,
        })
      }
    } catch (error) {
      // Error handling is now done in the mutation hook
      console.error("Failed to submit order:", error)
      // Remove any loading message element
      document.querySelectorAll('.sr-only[role="status"]').forEach((el) => {
        document.body.removeChild(el)
      })
    }
  }

  const handleBackClick = () => {
    setShowConfirmModal(true)
  }

  const handleBack = async () => {
    try {
      refetchRegistrationData()

      if (localStorageData?.uuid) {
        deleteOrder.mutate(localStorageData.uuid)
      } else {
        // If no order exists, just navigate back
        navigateToSelectTickets()
      }
    } catch (error) {
      // Fallback navigation in case of unexpected error
      navigateToSelectTickets()
    }
  }

  // Set initial discount code from order data if it exists
  useEffect(() => {
    if (discount_code?.code) {
      setDiscountCode(discount_code.code)
    }
  }, [discount_code?.code])

  const handleApplyDiscount = () => {
    if (!discountCode.trim()) return
    applyDiscount.mutate(discountCode)
  }

  const handleRemoveDiscount = () => {
    removeDiscount.mutate()
    setDiscountCode("")
  }

  // Helper function to format discount information
  const getDiscountDisplay = () => {
    if (!discount_code) return null

    const { code, percent_off, amount_off } = discount_code

    if (percent_off) {
      const percentValue = parseFloat(percent_off)
      return {
        code,
        displayText: `(${percent_off}% OFF)`,
        amount: ((total * percentValue) / 100).toFixed(2),
      }
    }

    if (amount_off) {
      return {
        code,
        displayText: "",
        amount: amount_off.toFixed(2),
      }
    }

    return { code, displayText: "", amount: "0.00" }
  }

  // Get discount information if available
  const discountInfo = getDiscountDisplay()
  const hasDiscount = !!discountInfo

  // Format price from cents to currency unit
  const formatPrice = (priceInCents: number) => {
    return (priceInCents / 100).toFixed(2)
  }

  if (isPending || isTicketGroupsLoading) {
    return <TicketDetailsPlaceholder />
  }

  return (
    <div className="container px-4 py-8 mx-auto md:pb-8">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-8 md:flex-row">
            <div className="flex-1">
              <ContactInformation register={methods.register} />
              <div className="space-y-6">
                {isAdvancedFormsEnabled ? (
                  <AdvancedFormsHandler
                    orderData={orderData}
                    register={methods.register}
                    errors={methods.formState.errors}
                    watch={methods.watch}
                    setValue={methods.setValue}
                    typeRankOrder={localStorageData?.ticketTypeRankOrder}
                  />
                ) : (
                  <BasicFormsHandler
                    orderData={orderData}
                    register={methods.register}
                    getFieldState={methods.getFieldState}
                    typeRankOrder={localStorageData?.ticketTypeRankOrder}
                    watch={methods.watch}
                    setValue={methods.setValue}
                  />
                )}
              </div>
            </div>

            <div className="hidden w-80 md:block">
              <div className="sticky top-8 p-6 bg-white rounded-lg border shadow-sm border-progress_bar_background text-default_card_text">
                <h2
                  className="mb-4 text-2xl font-bold text-black"
                  id="order-summary-heading"
                >
                  {t("general.order_summary")}
                </h2>
                <div role="region" aria-labelledby="order-summary-heading">
                  <ul className="p-0 m-0 list-none">
                    {(() => {
                      // Group tickets by type
                      const ticketsByType: Record<string, any[]> = {}
                      const typeNames: string[] = []

                      orderData?.ticket_set.forEach((ticket: any) => {
                        const typeName = String(ticket.ticket_type)
                        if (!ticketsByType[typeName]) {
                          ticketsByType[typeName] = []
                          typeNames.push(typeName)
                        }
                        ticketsByType[typeName].push(ticket)
                      })

                      // Sort types by published id order preserved from select-tickets
                      const rankOrder: number[] =
                        localStorageData?.ticketTypeRankOrder || []
                      const sortedTypeNames = [...typeNames].sort((a, b) => {
                        const aPublished =
                          ticketsByType[a]?.[0]?.ticket_type_published
                        const bPublished =
                          ticketsByType[b]?.[0]?.ticket_type_published
                        const aIdx =
                          aPublished != null
                            ? rankOrder.indexOf(aPublished)
                            : -1
                        const bIdx =
                          bPublished != null
                            ? rankOrder.indexOf(bPublished)
                            : -1
                        const aRank =
                          aIdx === -1 ? Number.POSITIVE_INFINITY : aIdx
                        const bRank =
                          bIdx === -1 ? Number.POSITIVE_INFINITY : bIdx
                        return aRank - bRank
                      })

                      return sortedTypeNames.map((typeName) => {
                        const details = {
                          count: ticketsByType[typeName].length,
                          price: ticketsByType[typeName][0].price,
                          name: ticketsByType[typeName][0].ticket_name,
                        }
                        return (
                          <li
                            key={typeName}
                            className="flex justify-between mb-2"
                          >
                            <span
                              aria-label={`${details.count} ${details.name} tickets`}
                            >
                              {details.count} x {details.name}
                            </span>
                            <span
                              aria-label={`${currencySymbol}${formatPrice(
                                details.price * details.count
                              )}`}
                            >
                              {currencySymbol}
                              {formatPrice(details.price * details.count)}
                            </span>
                          </li>
                        )
                      })
                    })()}
                  </ul>
                  <div className="pt-4 mt-4 border-t border-progress_bar_background">
                    <div
                      className="flex justify-between mb-4"
                      aria-live="polite"
                    >
                      <h3 className="text-black" id="subtotal-heading">
                        {t("general.subtotal")}
                      </h3>
                      <div
                        className="text-black"
                        aria-labelledby="subtotal-heading"
                      >
                        {currencySymbol}
                        {amount_subtotal
                          ? formatPrice(amount_subtotal)
                          : "0.00"}
                      </div>
                    </div>

                    {/* Discount code section */}
                    <div className="mb-4">
                      {!hasDiscount ? (
                        /* Discount code input - only show when no discount is applied */
                        <div className="relative mb-2">
                          <input
                            type="text"
                            className="p-2 pr-14 w-full text-sm rounded-md border border-input_border"
                            placeholder={
                              t("discount.enter_code") || "Enter discount code"
                            }
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                handleApplyDiscount()
                              }
                            }}
                            disabled={applyDiscount.isPending}
                            aria-label={
                              t("discount.code_input") || "Discount code input"
                            }
                          />
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 text-sm transform -translate-y-1/2 text-theme_accent hover:text-theme_accent_light disabled:text-gray-300 disabled:cursor-not-allowed"
                            onClick={handleApplyDiscount}
                            disabled={
                              applyDiscount.isPending || !discountCode.trim()
                            }
                            aria-label={
                              t("discount.apply") || "Apply discount code"
                            }
                          >
                            {applyDiscount.isPending
                              ? "..."
                              : t("discount.apply") || "Apply"}
                          </button>
                        </div>
                      ) : (
                        /* Show applied discount details */
                        <div className="p-3 mb-3 text-sm bg-gray-50 rounded-md">
                          <div className="flex justify-between items-center mb-1">
                            <div className="flex items-center">
                              <span className="font-medium">
                                {discountInfo.code}
                              </span>
                              {discountInfo.displayText && (
                                <span className="ml-1 text-theme_accent">
                                  {discountInfo.displayText}
                                </span>
                              )}
                            </div>
                            <span className="text-red-500">
                              -{currencySymbol}
                              {formatPrice(amount_discount || 0)}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="mt-1 text-xs text-red-500 hover:text-red-700"
                            onClick={handleRemoveDiscount}
                            disabled={removeDiscount.isPending}
                            aria-label={
                              t("discount.remove") || "Remove discount code"
                            }
                          >
                            {t("discount.remove") || "Remove discount"}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Tax line */}
                    <div className="flex justify-between mb-4 text-md">
                      <span>{t("general.sales_tax") || "Sales tax"}</span>
                      <span className="text-sm text-gray-400">
                        {t("general.tax_calculation") ||
                          "Calculated at checkout"}
                      </span>
                    </div>

                    {/* Final total */}
                    <div
                      className="flex justify-between mb-4 text-lg font-semibold"
                      aria-live="polite"
                    >
                      <span>{t("general.total") || "Total"}</span>
                      <span>
                        {currencySymbol}
                        {total ? formatPrice(total) : "0.00"}
                      </span>
                    </div>

                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={handleBackClick}
                        className="px-6 py-2 w-full bg-gray-200 rounded-md hover:bg-gray-300"
                        aria-label={`${t("general.back")} to ticket selection`}
                      >
                        {t("general.back")}
                      </button>
                      <button
                        type="submit"
                        disabled={!methods.formState.isValid}
                        className="px-6 py-2 w-full rounded-md bg-theme_accent text-theme_accent_contrast hover:bg-theme_accent_light disabled:bg-gray-300 disabled:cursor-not-allowed"
                        aria-busy={methods.formState.isSubmitting}
                        aria-label={
                          methods.formState.isSubmitting
                            ? t("general.processing")
                            : methods.formState.isValid
                            ? `${t("details.place_order")} with ${t(
                                "general.total"
                              )} of ${currencySymbol}${
                                total ? formatPrice(total) : "0.00"
                              }`
                            : `${t(
                                "details.place_order"
                              )} - please complete required fields`
                        }
                      >
                        {methods.formState.isSubmitting
                          ? `${t("general.processing")}...`
                          : t("details.place_order")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-white rounded-md border shadow-lg border-progress_bar_background md:hidden">
              <div
                className="container p-4 mx-auto"
                role="region"
                aria-label={t("general.order_summary")}
              >
                {/* Discount code section for mobile */}
                {!hasDiscount ? (
                  <div className="relative mb-4">
                    <input
                      type="text"
                      className="p-2 pr-14 w-full text-sm rounded-md border border-input_border"
                      placeholder={
                        t("discount.enter_code") || "Enter discount code"
                      }
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          handleApplyDiscount()
                        }
                      }}
                      disabled={applyDiscount.isPending}
                      aria-label={
                        t("discount.code_input") || "Discount code input"
                      }
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 text-sm transform -translate-y-1/2 text-theme_accent hover:text-theme_accent_light disabled:text-gray-300 disabled:cursor-not-allowed"
                      onClick={handleApplyDiscount}
                      disabled={applyDiscount.isPending || !discountCode.trim()}
                      aria-label={t("discount.apply") || "Apply discount code"}
                    >
                      {applyDiscount.isPending
                        ? "..."
                        : t("discount.apply") || "Apply"}
                    </button>
                  </div>
                ) : (
                  /* Show applied discount details on mobile */
                  <div className="p-3 mb-4 text-sm bg-gray-50 rounded-md">
                    <div className="flex justify-between items-center mb-1">
                      <div className="flex items-center">
                        <span className="font-medium">{discountInfo.code}</span>
                        {discountInfo.displayText && (
                          <span className="ml-1 text-theme_accent">
                            {discountInfo.displayText}
                          </span>
                        )}
                      </div>
                      <span className="text-red-500">
                        -{currencySymbol}
                        {formatPrice(amount_discount || 0)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="mt-1 text-xs text-red-500 hover:text-red-700"
                      onClick={handleRemoveDiscount}
                      disabled={removeDiscount.isPending}
                      aria-label={
                        t("discount.remove") || "Remove discount code"
                      }
                    >
                      {t("discount.remove") || "Remove discount"}
                    </button>
                  </div>
                )}

                {/* Subtotal in mobile */}
                <div className="flex justify-between items-center mb-2">
                  <span className="text-black" id="mobile-subtotal-heading">
                    {t("general.subtotal") || "Subtotal"}
                  </span>
                  <span
                    className="text-black"
                    aria-labelledby="mobile-subtotal-heading"
                  >
                    {currencySymbol}
                    {amount_subtotal ? formatPrice(amount_subtotal) : "0.00"}
                  </span>
                </div>

                {/* Tax in mobile */}
                <div className="flex justify-between items-center mb-2 text-md">
                  <span>{t("general.sales_tax") || "Sales tax"}</span>
                  <span className="text-sm text-gray-400">
                    {t("general.tax_calculation") || "Calculated at checkout"}
                  </span>
                </div>

                {/* Total in mobile */}
                <div
                  className="flex justify-between items-center mb-4"
                  aria-live="polite"
                >
                  <span
                    className="text-lg font-bold text-black"
                    id="mobile-total-heading"
                  >
                    {t("general.total") || "Total"}
                  </span>
                  <span
                    className="text-lg font-bold text-black"
                    aria-labelledby="mobile-total-heading"
                  >
                    {currencySymbol}
                    {total ? formatPrice(total) : "0.00"}
                  </span>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleBackClick}
                    className="flex-1 px-6 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                    aria-label={`${t("general.back")} to ticket selection`}
                  >
                    {t("general.back")}
                  </button>
                  <button
                    type="submit"
                    disabled={!methods.formState.isValid}
                    className="flex-1 px-6 py-2 rounded-md bg-theme_accent text-theme_accent_contrast hover:bg-theme_accent_light disabled:bg-gray-300 disabled:cursor-not-allowed"
                    aria-busy={methods.formState.isSubmitting}
                    aria-label={
                      methods.formState.isSubmitting
                        ? t("general.processing")
                        : methods.formState.isValid
                        ? `${t("details.place_order")} with ${t(
                            "general.total"
                          )} of ${currencySymbol}${
                            total ? formatPrice(total) : "0.00"
                          }`
                        : `${t(
                            "details.place_order"
                          )} - please complete required fields`
                    }
                  >
                    {methods.formState.isSubmitting
                      ? `${t("general.processing")}...`
                      : t("details.place_order")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
        <ConfirmationModal
          isOpen={showConfirmModal}
          onConfirm={handleBack}
          onCancel={() => setShowConfirmModal(false)}
        />
      </FormProvider>
    </div>
  )
}

export { TicketDetails }
